import * as React from 'react';
import './account-settings.scss';
import MembershipWithdrawalModal from './membership-withdrawal-modal/membership-withdrawal-modal';
import PlainNavbar from '../plainNavbar/plainNavbar';
import SuccessMessage from 'common/success-message/success-message';
import TextInput from 'common/text-input/text-input';
import Button from 'common/button/button';
import CalendarInput from 'common/calendar-input/calendar-input';

// Assets
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg';
import { ReactComponent as ProfileIcon } from 'assets/icons/profile.svg';
import { ReactComponent as ProfileEditIcon } from 'assets/icons/btn_edit.svg';

// Data
import accountDetails from './account-details.json';
import DateSelector from '../../common/date-selector/date-selector';
import useUserStore from 'zustands/useUserStore';
import useLoadingStore from 'zustands/useLoadingStore';
import { AxiosResponse } from 'axios';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import moment from 'moment';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import { milisecondToKoreanTime, phoneNumberAddHypen } from 'constants/utils';
import { HiEye, HiOutlineEyeOff } from 'react-icons/hi';
import { regExp_pwd } from 'constants/regExp';

interface MyInfo {
  name: string;
  birthday: string;
  tel: string;
}

interface PasswordReset {
  isHidden: boolean;
  newPassword: string;
  reNewPassword: string;
  authNumber: string;
}

export interface IAccountForm {
  name: string;
  birthDate: Date;
  phone: string;
  phoneSms: string;
}

let phoneAuthCountId: NodeJS.Timer | null = null;
let phonePwdAuthCountId: NodeJS.Timer | null = null;

const AccountSettings: React.FC = () => {
  const { userName, userId, userBirthDate = new Date(), setUserInfo } = useUserStore();

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const hiddenFileInput = React.useRef<any>(null);

  const [form, setForm] = React.useState<IAccountForm>({
    name: userName ?? '',
    birthDate: moment(userBirthDate).toDate(),
    phone: '',
    phoneSms: '',
  });
  const [phoneAuth, setPhoneAuth] = React.useState<boolean>(false); // 휴대폰번호 인증 인풋 표시여부
  const [phoneAuthCount, setPhoneAuthCount] = React.useState<number>(180); // 인증번호 카운트 값

  const [pwd, setPwd] = React.useState<PasswordReset>({
    isHidden: true,
    newPassword: '',
    reNewPassword: '',
    authNumber: '',
  });
  const [pwdPhoneAuth, setPwdPhoneAuth] = React.useState<boolean>(false); // 비밀번호 변경 시 휴대폰번호 인증 인풋 표시여부
  const [pwdPhoneAuthCount, setPwdPhoneAuthCount] = React.useState<number>(180); // 비밀번호 변경 인증번호 카운트 값

  const [changePasswordExpand, setChangePasswordExpand] = React.useState<boolean>(false); // 비밀번호 변경 모드
  const [membershipWithdrawalModal, setMembershipWithdrawalModal] = React.useState<boolean>(false); //회원탈퇴 모달

  const [successMessageModal, setSuccessMessageModal] = React.useState<boolean>(false);

  // 텍스트 인풋 입력 상태 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === 'phone' ? phoneNumberAddHypen(value.substring(0, 13)) : value,
    }));
  };

  // 비밀번호 텍스트 인풋 입력 상태 핸들러
  const handlePwdInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'newPassword') {
      setPwd((prev) => ({
        ...prev,
        [name]: value,
        reNewPassword: '',
      }));
    } else {
      setPwd((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // 날짜 선택기 입력 상태 핸들러
  const handleDateSelector = (date: Date) => {
    setForm((prev) => ({
      ...prev,
      birthDate: date,
    }));
  };

  // 이름 변경 요청
  const updateUserName = async () => {
    try {
      if (form.name.length === 0) {
        return setModalState({
          isModalShow: true,
          title: '',
          desc: '이름을 입력해 주세요',
        });
      }
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updateName`, {
        NAME: form.name,
      });
      console.log('/auth/updateName_data', data);

      if (data.resultCode === 0) {
        setUserInfo({ userName: form.name });
        setModalState({
          isModalShow: true,
          title: '이름 저장 완료',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/updateName');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 생년월일 변경 요청
  const updateUserBirthDate = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updateBirthdate`, {
        BIRTHDATE: moment(form.birthDate).format('YYYY-MM-DD'),
      });
      console.log('/auth/updateBirthdate_data', data);

      if (data.resultCode === 0) {
        setUserInfo({ userBirthDate: form.birthDate });
        setModalState({
          isModalShow: true,
          title: '생년월일 저장 완료',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/updateBirthdate');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 인증번호 카운팅
  const startPhoneAuthCount = () => {
    let time = 180;
    if (phoneAuthCountId !== null) {
      clearTimeout(phoneAuthCountId);
    }

    phoneAuthCountId = setInterval(() => {
      if (phoneAuthCountId && phoneAuthCount <= 0) {
        clearTimeout(phoneAuthCountId);
      }
      time = time - 1;
      setPhoneAuthCount(time);
    }, 1000);
  };

  // 비밀번호 변경 시 인증번호 카운팅
  const startPwdPhoneAuthCount = () => {
    let time = 180;
    if (phonePwdAuthCountId !== null) {
      clearTimeout(phonePwdAuthCountId);
    }

    phonePwdAuthCountId = setInterval(() => {
      if (phonePwdAuthCountId && pwdPhoneAuthCount <= 0) {
        clearTimeout(phonePwdAuthCountId);
      }
      time = time - 1;
      setPwdPhoneAuthCount(time);
    }, 1000);
  };

  // 휴대폰번호 인증요청
  const authPhoneNumber = async () => {
    if (form.phone.length !== 13) {
      return setModalState({
        isModalShow: true,
        title: '휴대폰번호 인증',
        desc: '휴대폰번호 11자리를 입력해 주세요',
      });
    }

    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/setMobileSms`, {
        PHONENUMBER: form.phone.split('-').join(''),
      });
      console.log('/auth/setMobileSms_data', data);

      if (data.resultCode === 0) {
        setForm((prev) => ({ ...prev, phoneSms: '' }));
        setPhoneAuth(true);
        startPhoneAuthCount();
        setModalState({
          isModalShow: true,
          title: '휴대폰번호 인증',
          desc: '인증번호가 카카오톡으로 전송되었습니다 (카카오톡 미사용 시 sms발송)',
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/setMobileSms');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 휴대폰번호 변경 요청
  const updatePhoneNumber = async () => {
    if (form.phone.length !== 13) {
      return setModalState({
        isModalShow: true,
        title: '휴대폰번호 인증요청',
        desc: '휴대폰번호 11자리를 입력해 주세요',
      });
    }
    if (form.phoneSms.length === 0) {
      return setModalState({
        isModalShow: true,
        title: '인증번호 미입력',
      });
    }
    if (phoneAuthCount <= 0) {
      return setModalState({
        isModalShow: true,
        title: '인증 제한시간 경과',
        desc: '다시 인증번호를 요청해 주세요',
      });
    }
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updateMobileNo`, {
        NEW_PHONENUMBER: form.phone.split('-').join(''),
        PHONENUMBER: userId,
        SMSCODE: form.phoneSms,
      });
      console.log('/auth/updateMobileNo_data', data);

      if (data.resultCode === 0) {
        if (phoneAuthCountId) clearTimeout(phoneAuthCountId);
        setPhoneAuth(false);
        setForm((prev) => ({ ...prev, phone: '' }));
        setUserInfo({ userId: form.phone.split('-').join('') });
        setModalState({
          isModalShow: true,
          title: '휴대폰번호 변경 완료',
        });
      } else if (data.resultCode === 1) {
        setModalState({
          isModalShow: true,
          title: '휴대폰번호 변경',
          desc: data.resultMsg,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/updateMobileNo');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 비밀번호 표시 토글
  const toggleHiddenPwd = () => {
    setPwd((prev) => ({ ...prev, isHidden: !prev.isHidden }));
  };

  // 비밀번호 유효성 검사
  const checkPwdValid = () => {
    if (pwd.newPassword !== pwd.reNewPassword) {
      return setModalState({
        isModalShow: true,
        title: '비밀번호 재설정',
        desc: '새로운 비밀번호와 재입력 비밀번호가 일치하지 않습니다',
      });
    }
    if (pwd.reNewPassword.length < 8 || pwd.reNewPassword.length > 20) {
      return setModalState({
        isModalShow: true,
        title: '비밀번호 재설정',
        desc: '비밀번호는 8~20자로 입력해 주세요 ',
      });
    }
    const regex = new RegExp(regExp_pwd);
    if (!regex.test(pwd.reNewPassword)) {
      return setModalState({
        isModalShow: true,
        title: '비밀번호 재설정',
        desc: '비밀번호는 영문, 숫자, 특수문자 모두 사용하여 입력해 주세요. (한글제외, 허용 특수문자: @$!%*#?&) ',
      });
    }
    return true;
  };

  // 비밀번호 - 휴대폰번호 인증 요청
  const authPwdPhoneNumber = async () => {
    try {
      if (!checkPwdValid()) return false;
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/setMobileSms`, {
        PHONENUMBER: userId,
      });
      console.log('/auth/setMobileSms_data', data);

      if (data.resultCode === 0) {
        setPwdPhoneAuth(true);
        startPwdPhoneAuthCount();
        setModalState({
          isModalShow: true,
          title: '휴대폰번호 인증',
          desc: `인증번호가 카카오톡으로 전송되었습니다 (카카오톡 미사용 시 sms발송)\n인증번호 입력 후 [비밀번호 저장] 버튼을 눌러 주세요`,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/setMobileSms');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 비밀번호 변경 취소
  const cancelResetPassword = () => {
    setPwd(() => ({
      isHidden: true,
      authNumber: '',
      newPassword: '',
      reNewPassword: '',
    }));
    setPwdPhoneAuth(false);
    setPwdPhoneAuthCount(180);
    setChangePasswordExpand(false);
  };

  // 비밀번호 변경 요청
  const handleResetPasswordSubmit = async () => {
    try {
      if (!checkPwdValid()) return false;
      if (pwd.authNumber.length !== 6) {
        return setModalState({
          isModalShow: true,
          title: '비밀번호 변경',
          desc: '휴대폰 인증번호 6자리를 입력해 주세요',
        });
      }
      if (pwdPhoneAuthCount <= 0) {
        return setModalState({
          isModalShow: true,
          title: '인증 제한시간 경과',
          desc: '다시 인증번호를 요청해 주세요',
        });
      }
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/updatePassword`, {
        PASSWORD: pwd.reNewPassword,
        PHONENUMBER: userId,
        SMSCODE: pwd.authNumber,
      });
      console.log('/auth/updatePassword_data', data);

      if (data.resultCode === 0) {
        setChangePasswordExpand(false);
        setPwdPhoneAuth(false);
        setPwdPhoneAuthCount(180);
        setModalState({
          isModalShow: true,
          title: '비밀번호 변경',
          desc: `완료되었습니다`,
        });
      } else if (data.resultCode === 1) {
        return setModalState({
          isModalShow: true,
          title: data.resultMsg,
          desc: `인증번호를 다시 입력해 주세요`,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/updatePassword');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const handlePasswordResetExpand = (event: any) => {
    event.preventDefault();
    setChangePasswordExpand(true);
  };

  const validatePasswordReset = (data: PasswordReset) => {
    if (
      data.authNumber !== '' &&
      /^[0-9]*$/.test(data.authNumber) &&
      data.newPassword === data.reNewPassword &&
      /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,}$/.test(data.newPassword)
    ) {
      return true;
    }
    return false;
  };

  const successMessageModalClose = () => {
    setSuccessMessageModal(false);
  };

  const toggleMembershipWithdrawalModal = () => {
    setMembershipWithdrawalModal(!membershipWithdrawalModal);
  };

  const submitWithdrawal = () => {
    setMembershipWithdrawalModal(false);
    // Send withdrawal data to backend
    setSuccessMessageModal(true);
  };

  const handleChange = (event: any) => {
    const fileUploaded = event.target.files[0];
    // handle profile image upload
  };

  const handleClick = (event: any) => {
    hiddenFileInput?.current?.click();
  };

  const handleSave = () => {
    // Save data
  };

  React.useEffect(() => {
    return () => {
      if (phoneAuthCountId) clearTimeout(phoneAuthCountId);
      if (phonePwdAuthCountId) clearTimeout(phonePwdAuthCountId);
    };
  }, []);

  return (
    <>
      <PlainNavbar backgroundColor="#f3f4f6" customButton={<></>}>
        <div className="account-settings">
          <div className="account-settings__head">
            <SettingsIcon />
            <div className="account-settings__head__head-text">계정설정</div>
          </div>
          <div className="account-settings__card-profile-changes">
            <div className="my-info">
              <div className="my-info__head">내 정보</div>
              <div className="my-info__row">
                {/* {accountDetails.profileImage ? (
                  <div className="my-info__profpic" onClick={handleClick}>
                    <ProfileEditIcon className="profile-icon-svg" />
                    <img src={accountDetails.profileImage} alt="" className="my-info__profpic__image" />
                  </div>
                ) : (
                  <div className="my-info__profpic" onClick={handleClick}>
                    <ProfileEditIcon className="profile-icon-svg" />
                    <ProfileIcon />
                  </div>
                )} */}
                <input type="file" ref={hiddenFileInput} onChange={handleChange} style={{ display: 'none' }} />
                <div className="my-info__form">
                  <form className="form">
                    <div className="form__name">
                      <div className="inputArea">
                        <TextInput
                          inputData={handleInputChange}
                          label="이름"
                          inputName="name"
                          value={form.name}
                          maxLength={6}
                        />
                      </div>
                      <div className="btnArea">
                        <Button label="저장" type="type2" height={40} action={updateUserName} />
                      </div>
                    </div>
                    <div className="form__birthday">
                      <div className="inputArea">
                        <DateSelector
                          onChange={handleDateSelector}
                          value={form.birthDate}
                          inputName="birthday"
                          label="생년월일"
                          height={56}
                        />
                      </div>
                      <div className="btnArea">
                        <Button label="저장" type="type2" height={40} action={updateUserBirthDate} />
                      </div>
                    </div>
                    <div className="form__tel">
                      <div className="inputArea">
                        <TextInput
                          inputData={handleInputChange}
                          label="휴대폰번호(ID)"
                          inputName="userId"
                          value={phoneNumberAddHypen(userId ?? '')}
                          maxLength={13}
                          disabled={true}
                        />
                      </div>
                    </div>
                    <div className="form__tel">
                      <div className="inputArea">
                        <TextInput
                          inputData={handleInputChange}
                          label="변경할 휴대폰번호(ID)"
                          inputName="phone"
                          value={form.phone}
                          maxLength={13}
                        />
                      </div>
                      <div className="phoneBtnArea">
                        <Button label="인증요청" type="type5" height={40} action={authPhoneNumber} />
                      </div>
                    </div>
                    {phoneAuth && (
                      <div className="form__tel">
                        <div className="inputArea">
                          <TextInput
                            inputData={handleInputChange}
                            label="인증번호"
                            inputName="phoneSms"
                            value={form.phoneSms}
                            maxLength={6}
                          />
                          <div className="count">
                            <span>{milisecondToKoreanTime(phoneAuthCount)}</span>
                          </div>
                        </div>
                        <div className="btnArea">
                          <Button label="변경" type="type2" height={40} action={updatePhoneNumber} />
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="account-settings__card-password-change">
            <div className="password">
              <div>비밀번호</div>
              <div className="password__row">
                <div className="password__form">
                  <form>
                    {!changePasswordExpand ? (
                      <div className="password-change">
                        <div className="password-change__input">
                          <TextInput
                            inputData={handleInputChange}
                            label="비밀번호*"
                            inputName="tel"
                            placeholder={'**************'}
                            disabled={true}
                          />
                        </div>
                        <div className="password-change__button">
                          <input
                            type="button"
                            value="비밀번호 변경"
                            className="password__submit-input"
                            onClick={handlePasswordResetExpand}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="change-password-form__enter-password">
                          <TextInput
                            label="비밀번호 재설정"
                            inputType={pwd.isHidden ? 'password' : 'text'}
                            value={pwd.newPassword}
                            inputData={handlePwdInputChange}
                            inputName="newPassword"
                            placeholder="새로운 비밀번호 입력"
                            maxLength={20}
                          />
                          <button type={'button'} className="hiddenBtn" onClick={toggleHiddenPwd}>
                            {pwd.isHidden ? <HiOutlineEyeOff /> : <HiEye />}
                          </button>
                        </div>
                        <div className="change-password-form__reenter-password" style={{ marginTop: 10 }}>
                          <div className="inputArea">
                            <TextInput
                              inputType={pwd.isHidden ? 'password' : 'text'}
                              value={pwd.reNewPassword}
                              inputData={handlePwdInputChange}
                              inputName="reNewPassword"
                              placeholder="새로운 비밀번호 재입력"
                              maxLength={20}
                            />
                          </div>
                          <div className="phoneBtnArea pwdAuth">
                            <Button label="휴대폰번호 인증" type="type5" height={40} action={authPwdPhoneNumber} />
                          </div>
                        </div>
                        <div className="change-password-form__message">
                          영문 숫자,특수문자 조합하여 8~20자로 입력해 주세요
                        </div>
                        {pwdPhoneAuth && (
                          <>
                            <div className="change-password-form__reenter-password">
                              <div className="inputArea">
                                <TextInput
                                  inputData={handlePwdInputChange}
                                  label="인증번호"
                                  inputName="authNumber"
                                  value={pwd.authNumber}
                                  maxLength={6}
                                />
                                <div className="count">
                                  <span>{milisecondToKoreanTime(pwdPhoneAuthCount)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="change-password-submit">
                              <div className="change-password-submit__cancel">
                                <Button label="취소" type="type1" height={60} action={cancelResetPassword} />
                              </div>
                              <div>
                                <Button
                                  label="비밀번호 변경"
                                  type="type2"
                                  height={60}
                                  action={handleResetPasswordSubmit}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="account-withdrawal" onClick={toggleMembershipWithdrawalModal}>
            더 이상 서비스 사용을 원치 않으신다면 <span className="account-withdrawal__button">회원탈퇴</span>
          </div> */}
        </div>
        {membershipWithdrawalModal && (
          <MembershipWithdrawalModal onClose={toggleMembershipWithdrawalModal} withdraw={submitWithdrawal} />
        )}
      </PlainNavbar>
    </>
  );
};
export default AccountSettings;
